import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Clock, MapPin, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardFooter, CardHeader } from './ui/card';
import AddToListDropdown from './AddToListDropdown';
import { isFavorite } from '../data/mock';
import { categoryColor, difficultyColor, distanceCategory } from '../utils/categoryLabels';
import walkUrl from '../utils/walkUrl';

const WalkCard = ({ walk, onFavoriteToggle }) => {
  const handleListChange = () => {
    // Trigger refresh in parent component
    if (onFavoriteToggle) onFavoriteToggle();
  };

  const favorite = isFavorite(walk.id);

  return (
    <Card className="group hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden flex flex-col h-full">
      <div className="relative">
        <Link to={walkUrl(walk)} className="block">
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={walk.photos?.[0] || walk.main_image || '/placeholder-walk.jpg'}
              alt={walk.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 cursor-pointer"
            />
          </div>
        </Link>
        <div className="absolute top-3 right-3 flex gap-1.5">
          <Badge
            className="text-white text-xs font-semibold border-0"
            style={{ backgroundColor: difficultyColor(walk.difficulty) }}
          >
            {walk.difficulty}{walk.distanceCategory ? ` · ${walk.distanceCategory}` : ''}
          </Badge>
        </div>
        <div className="absolute bottom-3 left-3">
          <Link 
            to={`/walks?category=${encodeURIComponent(walk.category)}`}
            onClick={(e) => {
              e.stopPropagation();
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 100);
            }}
            className="inline-block"
          >
            <Badge
              className="text-white text-xs font-semibold border-0 hover:opacity-90 cursor-pointer transition-opacity"
              style={{ backgroundColor: categoryColor(walk.category) }}
            >
              {walk.category}
            </Badge>
          </Link>
        </div>
      </div>
      
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <Link to={walkUrl(walk)} className="flex-1">
            <h3 className="font-bold text-lg leading-tight hover:text-blue-600 transition-colors">
              {walk.title}
            </h3>
          </Link>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {walk.duration}
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {walk.distance}
          </div>
          {(walk.userRatings || walk.rating) > 0 && (
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              {(walk.userRatings || walk.rating).toFixed(1)}
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="pt-0 flex-1">
        <p className="text-gray-600 line-clamp-3 leading-relaxed">
          {walk.practicalSummary}
        </p>
      </CardContent>

      <CardFooter className="pt-0 mt-auto">
        <div className="flex items-center justify-between w-full gap-2 min-w-0">
          <span className="text-sm text-gray-500 truncate shrink min-w-0">
            {walk.favoriteCount > 0 ? `${walk.favoriteCount} saves` : walk.region || ''}
            {walk.completionCount > 0 && <span className="ml-2">&middot; {walk.completionCount} completed</span>}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <AddToListDropdown
              walkId={walk.id}
              onListChange={handleListChange}
            />
            <Link to={walkUrl(walk)}>
              <Button variant="outline" size="sm" className="hover:bg-black hover:text-white transition-colors whitespace-nowrap">
                View Details
              </Button>
            </Link>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default WalkCard;